import { DateTime } from "luxon";
import Papa from "papaparse";

// Sort array by property
export const sortArrayByProperty = (data, property, order = "ascending") => {
  return [...data].sort((a, b) => {
    let aValue = a[property];
    let bValue = b[property];

    if (aValue === null || aValue === undefined) aValue = "";
    if (bValue === null || bValue === undefined) bValue = "";

    if (typeof aValue === "string") aValue = aValue.toLowerCase();
    if (typeof bValue === "string") bValue = bValue.toLowerCase();

    if (aValue < bValue) return order === "ascending" ? -1 : 1;
    if (aValue > bValue) return order === "ascending" ? 1 : -1;
    return 0;
  });
};

// Transform platform analytics data
export const transformPlatformAnalytics = (redGreenAnalytics) => {
  if (!redGreenAnalytics?.platform_wise) return [];

  return redGreenAnalytics.platform_wise.map((item, index) => {
    const redPercentage =
      item.total_apps > 0
        ? Math.round((item.app_count_red_channel / item.total_apps) * 100)
        : 0;

    return {
      id: `platform-${index}`,
      platform: item.platform || "N/A",
      total_apps: item.total_apps,
      red_channel: item.app_count_red_channel,
      green_channel: item.app_count_green_channel,
      red_percentage: redPercentage,
    };
  });
};

// Transform portfolio analytics data
export const transformPortfolioAnalytics = (redGreenAnalytics) => {
  if (!redGreenAnalytics?.portfolio_wise) return [];

  return redGreenAnalytics.portfolio_wise.map((item, index) => {
    const redPercentage =
      item.total_apps > 0
        ? Math.round((item.app_count_red_channel / item.total_apps) * 100)
        : 0;

    return {
      id: `portfolio-${index}`,
      portfolio: item.portfolio || "N/A",
      total_apps: item.total_apps,
      red_channel: item.app_count_red_channel,
      green_channel: item.app_count_green_channel,
      red_percentage: redPercentage,
    };
  });
};

// Filter channel data based on active tab
export const filterChannelData = (redGreenChannelData, activeChannelTab) => {
  if (!Array.isArray(redGreenChannelData)) return [];

  if (activeChannelTab === "Red Channel") {
    return redGreenChannelData.filter((item) => item.status === "Red");
  } else if (activeChannelTab === "Moved to Green Channel") {
    return redGreenChannelData.filter((item) => item.status === "Green");
  }
  return redGreenChannelData;
};

// Transform channel data for table display
export const transformChannelData = (paginatedData, sortColumn, sortDirection) => {
  const sortedData = sortArrayByProperty(paginatedData, sortColumn, sortDirection);

  return sortedData.map((item, index) => {
    const formatDate = (dateValue) => {
      if (!dateValue) return "-";
      const date = DateTime.fromISO(dateValue);
      return date.isValid ? date.toFormat("dd-MM-yyyy") : "-";
    };

    return {
      id: `channel-${item.app_id}-${index}`,
      app_id: item.app_id || "-",
      app_name: item.app_name || "-",
      platform: item.platform || "-",
      portfolio: item.portfolio || "-",
      status: item.status || "-",
      trigger_type: item.trigger_type || "-",
      incident: item.incident || "-",
      priority: item.priority || "-",
      incident_dates: item.incident_dates || "-",
      added_to_red_channel: formatDate(item.added_to_red_channel),
      moved_to_green_channel_on: formatDate(item.moved_to_green_channel_on),
      first_review: formatDate(item.first_review),
      final_review: formatDate(item.final_review),
      application_representative: item.application_representative || "-",
      deployment_type: item.deployment_type || "-",
      deployment_details: item.deployment_details || "-",
      comments: item.comments || "-",
      _originalData: item,
    };
  });
};

// Download CSV helper
export const downloadCSV = (data, filename) => {
  const csv = Papa.unparse(data);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Format data for download
export const formatDataForDownload = (data) => {
  return data.map((item) => {
    const formatDate = (dateValue) => {
      if (!dateValue) return "";
      const date = DateTime.fromISO(dateValue);
      return date.isValid ? date.toFormat("dd-MM-yyyy") : "";
    };

    // Use _originalData for dates to get the raw ISO format
    const originalData = item._originalData || item;

    return {
      "App ID": item.app_id || "",
      "App Name": item.app_name || "",
      Platform: item.platform || "",
      Portfolio: item.portfolio || "",
      Status: item.status || "",
      Trigger: item.trigger_type || "",
      Incident: item.incident || "",
      Priority: item.priority || "",
      "Incident Date": item.incident_dates || "",
      "Added to Red Channel": formatDate(originalData.added_to_red_channel),
      "Moved to Green Channel": formatDate(originalData.moved_to_green_channel_on),
      "First Review": formatDate(originalData.first_review),
      "Final Review": formatDate(originalData.final_review),
      "Application Representative": item.application_representative || "",
      "Deployment Type": item.deployment_type || "",
      "Deployment Details": item.deployment_details || "",
      Comments: item.comments || "",
    };
  });
};
